import React from 'react';
import {Layout, Typography, Button} from 'antd';
import {useNavigate} from 'react-router-dom';
import Header from "../../../components/molecules/Header/component";
import {useIsMobile} from '../../../utils/helpers';
import Footer from "../../../components/molecules/Footer/component";
import Seo from '../../../components/atoms/Seo/component';

const {Content} = Layout;
const {Title, Paragraph} = Typography;

const AboutPage: React.FC = () => {
    const navigate = useNavigate();
    const isMobile = useIsMobile();

    return (
        <div>
            <Seo
                title="About Promotify"
                description="Learn how Promotify helps advertisers run performance-based campaigns while promoters and partners earn from verified campaign activity."
                canonicalPath="/about"
            />
            <Header/>
            <Content style={{maxWidth: 900, margin: '0 auto', padding: '32px 16px 48px'}}>
                <article>
                    <header>
                        <Title level={2}>About Promotify</Title>
                        <Paragraph>
                            Promotify is a SaaS marketing platform that helps businesses run performance-based
                            promotional campaigns and track results. Advertisers create campaigns, fund them at
                            checkout, and use
                            the Promotify dashboard to monitor clicks and conversions in real time.
                        </Paragraph>

                        <Paragraph>
                            Customers are <strong>charged immediately at checkout</strong> for subscription plans or
                            campaign
                            funding. Subscriptions renew automatically on the chosen billing interval until cancelled.
                        </Paragraph>
                    </header>

                    <section>
                        <Title level={3}>What advertisers receive</Title>
                        <Paragraph>
                            When an advertiser purchases a subscription or funds a campaign, they receive access to our
                            campaign builder, analytics dashboard, and reporting tools. Advertisers only pay for
                            verified
                            clicks according to the campaign settings.
                        </Paragraph>
                    </section>

                    <section>
                        <Title level={3}>Promoters & partners</Title>
                        <Paragraph>
                            Promoters share unique referral links and earn rewards for verified clicks they drive.
                            Partners can
                            additionally earn a percentage of subscription revenue from users they recruit. Promotify
                            verifies
                            campaign activity and applies manual and automated fraud checks before awarding earnings.
                        </Paragraph>
                        <Paragraph>
                            Payouts to promoters and partners are distributed after verification and settlement
                            processes are
                            completed. If you are expecting a payout, you will see the status and payout history in your
                            wallet
                            dashboard.
                        </Paragraph>
                    </section>

                    <section>
                        <Title level={3}>Billing, refunds & cancellations</Title>
                        <Paragraph>
                            Billing is processed immediately at checkout. Subscriptions renew automatically until
                            cancelled.
                            You can cancel anytime from your dashboard: <strong>Settings → Manage billing → Cancel
                            subscription</strong>.
                            Cancelling stops future billing but does not retroactively refund prior billing periods.
                        </Paragraph>

                        <Paragraph>
                            For refund requests or billing disputes, please visit our <a
                            href="/refunds">Refunds</a> page or
                            contact support at <a href="mailto:info@promotifyai.com">info@promotifyai.com</a>.
                        </Paragraph>
                    </section>

                    <section>
                        <Title level={3}>Contact & support</Title>
                        <Paragraph>
                            For general enquiries and support, contact us at{' '}
                            <a href="mailto:info@promotifyai.com">info@promotifyai.com</a>.
                        </Paragraph>
                        <Paragraph>
                            Registered office: Promotify AI Ltd, 210 Leigh Road, Leigh-on-Sea, England, SS9 1BS.
                        </Paragraph>
                    </section>

                    <footer
                        style={{
                            marginTop: 24,
                            display: 'flex',
                            gap: 8,
                            flexDirection: isMobile ? 'column' : 'row',
                            justifyContent: isMobile ? 'stretch' : 'flex-end'
                        }}
                    >
                        <Button
                            type="default"
                            onClick={() => navigate('/contact')}
                            block={isMobile}
                            style={{width: isMobile ? '100%' : 'auto'}}
                        >
                            Contact support
                        </Button>
                    </footer>
                </article>
            </Content>
            <Footer/>
        </div>
    );
};

export default AboutPage;
